import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Typography,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, CircularProgress } from "@mui/material";
import { Seller } from "../../pages";
import useUserStore from "../../../../store/user";
import { FollowingProfilesService } from "../../../following-profiles/services";
import { SellersService } from "../../services";
import type { TSellerProfileAccess } from "../../pages/seller-payload";

type ProfileOption = {
  publicId: string;
  name: string;
};

type ProfileAccessDraft = {
  profilePublicId: string;
  name: string;
  accessLevel: "VIEW" | "EDIT";
};

interface SellerFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (seller: {
    name: string;
    email: string;
    password?: string;
    level: string;
    status: string;
    id?: string;
    profileAccesses?: TSellerProfileAccess[];
  }) => void;
  seller: Seller | null;
}

const levelDescriptions: Record<string, string> = {
  GMAIL_MANAGER:
    "Conta Google usada para conectar/importar perfis. Não é um usuário humano de login do sistema.",
  PORTFOLIO_MANAGER:
    "Supervisiona gestores de contas e tem acesso a configurações avançadas.",
  ADMIN:
    "Acesso administrativo com permissões para gerenciar usuários e configurações gerais do sistema.",
  STAFF: "Acesso básico para vendedores.",
  CUSTOMER: "Acesso restrito a clientes.",
};

export const SellerFormModal: React.FC<SellerFormModalProps> = ({
  open,
  onClose,
  onSave,
  seller,
}) => {
  const { user } = useUserStore();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    level: "GMAIL_MANAGER" as string,
    status: "active" as string,
  });
  const [errors, setErrors] = useState({
    name: false,
    email: false,
    password: false,
  });
  const [profileOptions, setProfileOptions] = useState<ProfileOption[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [profileAccesses, setProfileAccesses] = useState<ProfileAccessDraft[]>(
    [],
  );
  const [accessesTouched, setAccessesTouched] = useState(false);

  useEffect(() => {
    const shouldLoad =
      open && formData.level === "CUSTOMER" && profileOptions.length === 0;
    if (!shouldLoad || !user?.token || loadingProfiles) return;

    let cancelled = false;
    const fetchProfiles = async () => {
      setLoadingProfiles(true);
      try {
        const data = await FollowingProfilesService.getFollowingProfiles({
          token: user.token,
          page: 0,
          size: 200,
          query: "",
        });
        if (!cancelled && data?.content) {
          setProfileOptions(
            data.content.map(
              (p: { profileBusinessId: string; razaoSocial?: string }) => ({
                publicId: p.profileBusinessId,
                name: p.razaoSocial || p.profileBusinessId,
              }),
            ),
          );
        }
      } finally {
        if (!cancelled) setLoadingProfiles(false);
      }
    };
    fetchProfiles();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, formData.level, user?.token]);

  useEffect(() => {
    if (seller) {
      let levelValue = "CUSTOMER";
      switch (seller.level) {
        case "Cliente":
          levelValue = "CUSTOMER";
          break;
        case "Supervisor de Contas":
          levelValue = "PORTFOLIO_MANAGER";
          break;
        case "Conta Google (importador)":
          levelValue = "GMAIL_MANAGER";
          break;
        case "Admin":
          levelValue = "ADMIN";
          break;
        case "Vendedor":
          levelValue = "STAFF";
          break;
        default:
          levelValue = "STAFF";
      }

      let statusValue = "active";
      if (
        seller.statusLabel === "Ativo" ||
        seller.status === ("active" as any) ||
        seller.status === ("success" as any)
      ) {
        statusValue = "active";
      } else if (
        seller.statusLabel === "Inativo" ||
        seller.status === ("inactive" as any) ||
        seller.status === ("disabled" as any)
      ) {
        statusValue = "inactive";
      }

      setFormData({
        name: seller.name,
        email: seller.email,
        password: "",
        level: levelValue,
        status: statusValue,
      });
    } else {
      setFormData({
        name: "",
        email: "",
        password: "",
        level: "STAFF",
        status: "active",
      });
    }
    setErrors({ name: false, email: false, password: false });
    setProfileAccesses([]);
    setAccessesTouched(false);
  }, [seller, open]);

  // Ao editar um cliente existente, pre-carrega os acessos atuais.
  useEffect(() => {
    if (!open || !seller?.id || !user?.token) return;

    let cancelled = false;
    const loadAccesses = async () => {
      const data = await SellersService.getProfileAccesses(
        seller.id,
        user.token,
      );
      if (!cancelled && Array.isArray(data)) {
        setProfileAccesses(
          data.map(
            (item: {
              profilePublicId: string;
              name?: string;
              accessLevel?: "VIEW" | "EDIT";
            }) => ({
              profilePublicId: item.profilePublicId,
              name: item.name || item.profilePublicId,
              accessLevel: item.accessLevel === "EDIT" ? "EDIT" : "VIEW",
            }),
          ),
        );
      }
    };
    loadAccesses();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seller?.id, user?.token]);

  const validate = () => {
    const newErrors = {
      name: !formData.name,
      email: !formData.email,
      password:
        (!seller && !formData.password) ||
        (!!formData.password && formData.password.length < 8),
    };
    setErrors(newErrors);
    return !newErrors.name && !newErrors.email && !newErrors.password;
  };

  const handleSave = () => {
    if (validate()) {
      // So envia profileAccesses quando o operador mexeu na secao.
      // undefined -> payload null -> backend nao altera os acessos atuais.
      const shouldSendAccesses =
        formData.level === "CUSTOMER" && accessesTouched;
      onSave({
        ...formData,
        id: seller?.id,
        profileAccesses: shouldSendAccesses
          ? profileAccesses.map(({ profilePublicId, accessLevel }) => ({
              profilePublicId,
              accessLevel,
            }))
          : undefined,
      });
    }
  };

  const handleAccessSelectionChange = (selected: ProfileOption[]) => {
    setAccessesTouched(true);
    setProfileAccesses((prev) =>
      selected.map((option) => {
        const existing = prev.find(
          (item) => item.profilePublicId === option.publicId,
        );
        return (
          existing ?? {
            profilePublicId: option.publicId,
            name: option.name,
            accessLevel: "VIEW",
          }
        );
      }),
    );
  };

  const handleAccessLevelChange = (
    profilePublicId: string,
    accessLevel: "VIEW" | "EDIT",
  ) => {
    setAccessesTouched(true);
    setProfileAccesses((prev) =>
      prev.map((item) =>
        item.profilePublicId === profilePublicId
          ? { ...item, accessLevel }
          : item,
      ),
    );
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name as string]: value }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, fontWeight: "bold" }}>
        {seller ? "Editar Usuário" : "Novo Usuário"}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography gutterBottom>
          Preencha os dados para {seller ? "editar o" : "criar um novo"} usuário
          no sistema
        </Typography>
        <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Nome Completo"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleChange}
            error={errors.name}
            helperText={errors.name && "Nome é obrigatório"}
            required
          />
          <TextField
            margin="dense"
            name="email"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
            helperText={errors.email && "Email é obrigatório"}
            required
          />
          <TextField
            margin="dense"
            name="password"
            label={seller ? "Nova senha (opcional)" : "Senha inicial"}
            type="password"
            fullWidth
            variant="outlined"
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
            helperText={
              errors.password
                ? seller
                  ? "Informe pelo menos 8 caracteres ou deixe em branco para nao alterar."
                  : "Senha inicial obrigatoria, com pelo menos 8 caracteres."
                : seller
                  ? "Preencha apenas se quiser redefinir a senha deste usuario."
                  : "Use uma senha temporaria para o cliente acessar ate o e-mail estar integrado."
            }
            required={!seller}
            autoComplete="new-password"
          />
          <FormControl fullWidth margin="dense" required>
            <InputLabel>Função</InputLabel>
            <Select
              name="level"
              value={formData.level}
              label="Função"
              onChange={handleChange as any}
            >
              <MenuItem value="CUSTOMER">Cliente</MenuItem>
              <MenuItem value="PORTFOLIO_MANAGER">
                Supervisor de Contas
              </MenuItem>
              <MenuItem value="GMAIL_MANAGER">Conta Google (importador)</MenuItem>
              <MenuItem value="ADMIN">Admin</MenuItem>
              <MenuItem value="STAFF">Vendedor</MenuItem>
            </Select>
            <FormHelperText>{levelDescriptions[formData.level]}</FormHelperText>
          </FormControl>
          {formData.level === "CUSTOMER" && (
            <Box sx={{ mt: 1, mb: 1 }}>
              <Autocomplete
                multiple
                options={profileOptions}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) =>
                  option.publicId === value.publicId
                }
                value={profileAccesses.map((item) => ({
                  publicId: item.profilePublicId,
                  name: item.name,
                }))}
                onChange={(_, selected) =>
                  handleAccessSelectionChange(selected)
                }
                loading={loadingProfiles}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="dense"
                    label="Perfis do cliente"
                    placeholder="Buscar perfil..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingProfiles ? (
                            <CircularProgress color="inherit" size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <FormHelperText>
                {seller
                  ? "Lista reflete os acessos atuais. Alterações substituem todos os acessos do cliente ao salvar."
                  : "Selecione os perfis que este cliente podera acessar e a permissao de cada um."}
              </FormHelperText>
              {profileAccesses.map((item) => (
                <Box
                  key={item.profilePublicId}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mt: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                    {item.name}
                  </Typography>
                  <Select
                    size="small"
                    value={item.accessLevel}
                    onChange={(e) =>
                      handleAccessLevelChange(
                        item.profilePublicId,
                        e.target.value as "VIEW" | "EDIT",
                      )
                    }
                  >
                    <MenuItem value="VIEW">Ver</MenuItem>
                    <MenuItem value="EDIT">Editar</MenuItem>
                  </Select>
                </Box>
              ))}
            </Box>
          )}
          <FormControl fullWidth margin="dense" required>
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={formData.status}
              label="Status"
              onChange={handleChange as any}
            >
              <MenuItem value={"active"}>Ativo</MenuItem>
              <MenuItem value="inactive">Inativo</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: "16px 24px" }}>
        <Button onClick={onClose} variant="outlined" color="secondary">
          Cancelar
        </Button>
        <Button onClick={handleSave} variant="contained">
          {seller ? "Salvar Alterações" : "Criar Usuário"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
